import { ChevronDown, Search, X } from "lucide-react";
import { PAGE_CONTAINER } from "../../layout/pageWidth";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const titleCase = (s) =>
  typeof s === "string" && s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/**
 * The API validates `sort` against ["recent", "popular", "highest_rated"] —
 * there is no "trending" mode, so the design's Trending label maps onto
 * `popular`, which is the closest ranking the server actually implements.
 */
const SORTS = [
  { value: "popular", label: "Trending" },
  { value: "recent", label: "Newest" },
  { value: "highest_rated", label: "Highest rated" },
];

const Select = ({ label, value, onChange, children }) => (
  <div className="relative shrink-0">
    <label className="sr-only">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className={`appearance-none cursor-pointer rounded-full border py-2 pl-4 pr-9 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500/30 ${
        value
          ? "border-teal-200 bg-teal-50 text-teal-800"
          : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
      }`}
    >
      {children}
    </select>
    <ChevronDown
      size={14}
      className={`pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 ${
        value ? "text-teal-600" : "text-gray-400"
      }`}
    />
  </div>
);

const CommunityFilterBar = ({ filters, onChange, onReset, sort, onSortChange, facets, searchValue, onSearchChange, onSearchSubmit, onSearchClear }) => {
  // Every option is a { value, label } pair because for trip types the two
  // differ: the API validates `tripType` against a LOWERCASE list with a
  // case-sensitive check, so sending the display casing ("Solo") returns
  // 400 INVALID_TRIP_TYPE. Value must be the lowercase code.
  const tripTypes = facets?.tripTypes?.length
    ? facets.tripTypes
        .filter((t) => t.type)
        .map((t) => ({ value: t.type, label: t.label || titleCase(t.type) }))
    : ["family", "couple", "solo", "friends", "honeymoon", "senior", "adventure"].map(
        (v) => ({ value: v, label: titleCase(v) }),
      );

  // For budgets and durations the bucket label IS the filter value, so both
  // sides are the same string — including the en-dash used server-side.
  const budgets = facets?.budgetBuckets?.length
    ? facets.budgetBuckets.filter((b) => b.label).map((b) => ({ value: b.label, label: b.label }))
    : ["Under ₹25k", "₹25k–₹50k", "₹50k–₹1L", "Over ₹1L"].map((v) => ({ value: v, label: v }));

  const durations = facets?.durations?.length
    ? facets.durations.filter((d) => d.label).map((d) => ({ value: d.label, label: d.label }))
    : ["1–3 days", "4–7 days", "8–14 days", "15+ days"].map((v) => ({ value: v, label: v }));

  // The API sends a months facet with counts; it expects an integer 1–12.
  const months = facets?.months?.length
    ? facets.months
        .filter((m) => m.month)
        .map((m) => ({
          value: String(m.month),
          label: m.label || MONTH_NAMES[m.month - 1],
        }))
    : MONTH_NAMES.map((label, i) => ({ value: String(i + 1), label }));

  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    // Sticks directly beneath the fixed navbar (h-16, h-20 at lg).
    <div className="sticky top-16 z-30 border-y border-gray-200 bg-white/95 backdrop-blur-sm lg:top-20">
      {/* One scrollable row on mobile instead of wrapping to several lines;
          reverts to the normal wrapping layout from sm: up where there's
          room for everything at once. */}
      <div className={`${PAGE_CONTAINER} flex flex-nowrap items-center gap-3 overflow-x-auto scrollbar-hidden py-3 sm:flex-wrap sm:overflow-visible`}>
        <Select label="Group Type" value={filters.tripType} onChange={(v) => onChange("tripType", v)}>
          <option value="">Group Type</option>
          {tripTypes.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>

        <Select label="Month" value={filters.month} onChange={(v) => onChange("month", v)}>
          <option value="">Month</option>
          {months.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>

        <Select label="Budget" value={filters.budget} onChange={(v) => onChange("budget", v)}>
          <option value="">Budget</option>
          {budgets.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>

        <Select label="Duration" value={filters.duration} onChange={(v) => onChange("duration", v)}>
          <option value="">Duration</option>
          {durations.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>

        {activeCount > 0 && (
          <>
            <span className="hidden h-5 w-px bg-gray-200 sm:block" aria-hidden="true" />
            <button
              type="button"
              onClick={onReset}
              className="shrink-0 cursor-pointer text-sm font-semibold text-teal-600 transition-colors hover:text-teal-700"
            >
              Reset Filters
            </button>
          </>
        )}

        {/* Sort by, then the search, at the far right of the row. On a phone the row slides sideways, so they come first there — a search
            at the far end of a scrolling row would never be found. */}
        <div className="flex shrink-0 items-center gap-3 max-sm:order-first sm:ml-auto">
          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden text-[11px] font-bold uppercase tracking-widest text-gray-400 sm:inline">
              Sort by
            </span>
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => onSortChange(e.target.value)}
                aria-label="Sort by"
                className="cursor-pointer appearance-none rounded-full border border-transparent bg-transparent py-2 pl-2 pr-7 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSearchSubmit();
            }}
            role="search"
            className="flex h-10 w-52 shrink-0 items-center gap-2 rounded-full border border-gray-200 bg-gray-50 pl-3.5 pr-2 transition-colors focus-within:border-teal-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-teal-500/20 sm:w-56 lg:w-72"
          >
            <Search size={16} className="shrink-0 text-gray-400" aria-hidden="true" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search destinations or trips…"
              aria-label="Search community trips"
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
            />
            {searchValue && (
              <button
                type="button"
                onClick={onSearchClear}
                aria-label="Clear search"
                className="shrink-0 cursor-pointer rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommunityFilterBar;
